import { useMutation } from '@apollo/client';
import { debounce, kebabCase } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { AgendaBlockList } from '../../compositions/agenda/list/agenda-block-list';
import { AgendaAppointmentModal } from '../../compositions/agenda/modals/agenda-appointment-modal/agenda-appointment-modal';
import { AgendaNotesModal } from '../../compositions/agenda/modals/agenda-notes-modal/agenda-notes-modal';
import { AgendaTemplateHealthProfessionalApplyModal } from '../../compositions/agenda/modals/agenda-template-health-professional-apply-modal/agenda-template-health-professional-apply-modal';
import { useAppContext } from '../../data/context/app-context';
import { useAgendaItem } from '../../data/data/agenda/use-agenda-item';
import { AgendaAppointmentModalAction } from '../../data/data/enums';
import { useHealthProfessionalItems } from '../../data/data/health-professionals/use-health-professional-items';
import {
  BlockDayDocument,
  ConsultType,
  GetAgendaItemsDocument,
  HealthProfessionalItemDataFragment,
  SlotType,
} from '../../data/types/api/graphql';
import { formatDate } from '../../data/utils/date.utils';
import { useFormatters } from '../../data/utils/formatters.utils';
import { useNavigateWithHealthProvider } from '../../data/utils/router.utils';
import { Button, ButtonIcon, ButtonType } from '../../elements/button/button';
import { Content } from '../../elements/content/content';
import { ContentLoader } from '../../elements/content/content-loader';
import { ContentMessage } from '../../elements/content/content-message';
import { BaseInputIcon } from '../../elements/form/input/base-input';
import { InputField } from '../../elements/form/input/input-field';
import { InputFieldPager } from '../../elements/form/input-field-pager';
import { CustomFormattedMessage } from '../../elements/intl/custom-formatted-message';
import { Sidebar } from '../../elements/sidebar/sidebar';
import { SidebarList } from '../../elements/sidebar-list/sidebar-list';
import { SidebarListItem } from '../../elements/sidebar-list/sidebar-list-item';
import { Tag, TagType } from '../../elements/tag/tag';
import { useAppointmentModal } from '../../hooks/modal/use-appointment-modal';

export function AgendaPage() {
  const intl = useIntl();

  const { healthProviderId, user } = useAppContext();

  const { healthProfessionalId, date } = useParams();

  const [search, setSearch] = useState<string>('');

  const { appointmentModalData, showAppointmentModal } = useAppointmentModal();

  const [applyTemplateModalOpen, setApplyTemplateModalOpen] =
    useState<boolean>(false);

  const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false);

  const {
    data: healthProfessionalItemsData,
    loading: healthProfessionalItemsLoading,
  } = useHealthProfessionalItems({ healthProviderId, onlyAgenda: true });

  const healthProfessionalItems = useMemo(
    () => healthProfessionalItemsData?.items ?? [],
    [healthProfessionalItemsData]
  );

  const { data: agendaItemData, loading: agendaItemLoading } = useAgendaItem({
    healthProviderId,
    healthProfessionalId,
    date: date ?? formatDate(new Date()),
    search,
  });

  const navigate = useNavigateWithHealthProvider();

  // auto select current user
  useEffect(() => {
    if (healthProfessionalItemsData && user?.id && !healthProfessionalId) {
      const containsCurrentUser = healthProfessionalItemsData.items.find(
        x => x.id === user?.id
      );
      if (containsCurrentUser) {
        navigate(`/agenda/${user?.id}`);
      }
    }
  }, [healthProfessionalId, healthProfessionalItemsData, navigate, user?.id]);

  const onHandleCurrentDate = useCallback(() => {
    navigate(`/agenda/${healthProfessionalId}`);
  }, [healthProfessionalId, navigate]);

  const onHandleDate = useCallback(
    (value: string) => {
      navigate(`/agenda/${healthProfessionalId}/${value}`);
    },
    [healthProfessionalId, navigate]
  );

  const onHandleDatePager = useCallback(
    (type: 'prev' | 'next') => {
      const currentDate = date ? new Date(date) : new Date();

      if (type === 'prev') {
        currentDate.setDate(currentDate.getDate() - 1);
      }

      if (type === 'next') {
        currentDate.setDate(currentDate.getDate() + 1);
      }

      navigate(`/agenda/${healthProfessionalId}/${formatDate(currentDate)}`);
    },
    [date, healthProfessionalId, navigate]
  );

  const onHandleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const onHandleHealthProfessional = useCallback(
    (option: HealthProfessionalItemDataFragment) => {
      if (date) {
        navigate(`/agenda/${option.id}/${date}`);
      } else {
        navigate(`/agenda/${option.id}`);
      }
    },
    [date, navigate]
  );

  const { getFullName } = useFormatters();

  const hasSchedule = useMemo(
    () => agendaItemData !== null && agendaItemData?.hasSchedule === true,
    [agendaItemData]
  );

  const hasAgenda = useMemo(
    () =>
      agendaItemData !== null &&
      agendaItemData?.blocks.length > 0 &&
      agendaItemData?.hasSchedule,
    [agendaItemData]
  );

  const [blockDay, { loading: blockDayLoading }] = useMutation(
    BlockDayDocument,
    {
      refetchQueries: [GetAgendaItemsDocument],
    }
  );

  const onHandleBlockDay = useCallback(
    (block: boolean) => {
      blockDay({
        variables: {
          healthProviderId,
          healthProfessionalId,
          date: date ?? formatDate(new Date()),
          block,
        },
      });
    },
    [blockDay, date, healthProfessionalId, healthProviderId]
  );

  return (
    <>
      <Sidebar
        title={<CustomFormattedMessage id="agenda.title" />}
        toolbar={
          <>
            <Button
              icon={ButtonIcon.CALENDAR_ADD}
              onClick={() => {
                showAppointmentModal(SlotType.Common, {
                  action: AgendaAppointmentModalAction.CREATE,
                  healthProviderId,
                  agendaAppointmentItem: {
                    disableHonorific: false,
                    consultType: ConsultType.Physical,
                  },
                });
              }}
            >
              <CustomFormattedMessage id="agenda.button.create-appointment" />
            </Button>
            {user?.isAdmin && (
              <Button
                icon={ButtonIcon.LIST_ADD}
                onClick={() => setApplyTemplateModalOpen(true)}
              >
                <CustomFormattedMessage id="agenda.button.apply-template" />
              </Button>
            )}
          </>
        }
      >
        <SidebarList
          loading={healthProfessionalItemsLoading}
          items={healthProfessionalItems.map(option => (
            <SidebarListItem
              key={option.id}
              title={
                <>
                  {getFullName(option)}
                  <small>
                    <CustomFormattedMessage
                      id={`common.honorific.${kebabCase(option.honorific)}`}
                    />
                  </small>
                </>
              }
              active={healthProfessionalId === option.id}
              onClick={() => onHandleHealthProfessional(option)}
            />
          ))}
        />
      </Sidebar>

      <Content
        toolbar={
          healthProfessionalId ? (
            <>
              <Button inverted onClick={onHandleCurrentDate}>
                <CustomFormattedMessage id="common.today" />
              </Button>

              <InputFieldPager
                onClickPrev={() => onHandleDatePager('prev')}
                onClickNext={() => onHandleDatePager('next')}
              >
                <InputField
                  type="date"
                  name="date"
                  icon={BaseInputIcon.DATE}
                  value={date ?? formatDate(new Date())}
                  onChange={onHandleDate}
                />
              </InputFieldPager>

              {user?.isAdmin === true && hasSchedule && hasAgenda && (
                <Button
                  disabled={blockDayLoading}
                  type={ButtonType.SECONDARY}
                  icon={
                    agendaItemData?.patientBlocked
                      ? ButtonIcon.LOCK
                      : ButtonIcon.LOCK_OPEN
                  }
                  inverted
                  onClick={() => {
                    onHandleBlockDay(!agendaItemData?.patientBlocked);
                  }}
                >
                  {agendaItemData?.patientBlocked ? (
                    <CustomFormattedMessage id="common.agenda-day-unlocked" />
                  ) : (
                    <CustomFormattedMessage id="common.agenda-day-locked" />
                  )}
                </Button>
              )}

              <InputField
                icon={BaseInputIcon.SEARCH}
                type="search"
                name="search"
                placeholder={intl.formatMessage({
                  id: 'common.patients-search-placeholders',
                })}
                onChange={value => onHandleSearchDebounce(value)}
              />
            </>
          ) : undefined
        }
      >
        {!healthProfessionalId && (
          <ContentMessage
            message={<CustomFormattedMessage id="agenda.select-agenda" />}
          />
        )}

        {healthProfessionalId && (
          <>
            {agendaItemLoading && <ContentLoader />}

            {!agendaItemLoading && (
              <>
                {!hasSchedule ? (
                  <ContentMessage
                    message={<CustomFormattedMessage id="agenda.no-schedule" />}
                  />
                ) : (
                  !hasAgenda && (
                    <ContentMessage
                      message={<CustomFormattedMessage id="agenda.no-agenda" />}
                    />
                  )
                )}

                {hasSchedule && hasAgenda && (
                  <>
                    {user?.isAdmin && (
                      <div className="tags">
                        <Tag
                          type={TagType.TOOLTIP}
                          text={
                            <CustomFormattedMessage
                              id="agenda.template"
                              values={{
                                name: agendaItemData?.template?.name ?? (
                                  <CustomFormattedMessage id="agenda.template.not-available" />
                                ),
                              }}
                            />
                          }
                        />
                        {agendaItemData?.hasSchedule &&
                          !agendaItemData?.hasCorrectSchedule && (
                            <Tag
                              type={TagType.TOOLTIPERROR}
                              text={
                                <CustomFormattedMessage id="agenda.partial-schedule" />
                              }
                            />
                          )}
                        {!agendaItemData?.hasSchedule && (
                          <Tag
                            type={TagType.TOOLTIPERROR}
                            text={
                              <CustomFormattedMessage id="agenda.no-schedule" />
                            }
                          />
                        )}
                      </div>
                    )}

                    <AgendaBlockList
                      showAppointmentModal={showAppointmentModal}
                      healthProviderId={healthProviderId}
                      healthProfessionalId={healthProfessionalId}
                      date={date ?? formatDate(new Date())}
                      items={agendaItemData?.blocks ?? []}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </Content>

      {appointmentModalData.value && (
        <AgendaAppointmentModal {...appointmentModalData.value} />
      )}

      {notesModalOpen && (
        <AgendaNotesModal
          healthProviderId={healthProviderId}
          healthProfessionalId={healthProfessionalId}
          date={date ?? formatDate(new Date())}
          onClose={() => {
            setNotesModalOpen(false);
          }}
        />
      )}

      {applyTemplateModalOpen && (
        <AgendaTemplateHealthProfessionalApplyModal
          healthProviderId={healthProviderId}
          healthProfessionalId={healthProfessionalId}
          onClose={() => {
            setApplyTemplateModalOpen(false);
          }}
        />
      )}
    </>
  );
}
